import { useEffect, useRef, useState } from "react";
import { Flex, UploadFile } from "antd";
import { GetOneResponse, useTranslate } from "@refinedev/core";
import UploadFiles, {
  UploadFilesProps,
  UploadFilesRef,
} from "components/uploader/UploadFiles/";
import { RecursivePartial } from "components/uploader/types/";
import {
  AssetSource,
  CreateAssetResponse,
  CreateMediaAssetRequest,
  MediaAsset,
} from "pages/media/types";
import { Empty } from "components/Empty/";
import { useTemplateData } from "../../../../hooks/useTemplateData";
import { DragAndDropArea } from "../../../controls/DragAndDropArea";
import { InfiniteScrollList } from "../../ImagesVideosTabs/controls/InfiniteScrollList";
import { MusicAssetCard } from "./MusicAssetCard";

const CREATE_ASSET_REQUEST_TYPE = "CreateLibraryAsset";

export type MusicAssetListProps = {
  // undefined/null = nothing highlighted; string = a specific uploaded track
  selectedAssetId: string | null | undefined;
  onSelectAsset: (assetId: string | null) => void;
  volume?: number;
};

export const MusicAssetList = ({
  selectedAssetId,
  onSelectAsset,
  volume,
}: MusicAssetListProps) => {
  const t = useTranslate();
  const uploadRef = useRef<UploadFilesRef>(null);
  const [hasFilesInPreview, setHasFilesInPreview] = useState(false);
  const previewObjectUrls = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    const objectUrls = previewObjectUrls.current;
    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const {
    organizationId,
    assetLibrariesData: { organizationAssetLibraries },
    mediaAssetsData: {
      music,
      handleFileAdded,
      updateAllAssets,
      deleteAsset,
      fetchNextPage,
      hasMore,
      isLoadingAssetLibraryAssets,
    },
  } = useTemplateData();

  const assetLibraryId = organizationAssetLibraries[0]?.id;

  const rows: Partial<MediaAsset>[] = music;

  const uploadMusicProps: UploadFilesProps = {
    defaultItems: music,
    acceptedMimeTypes: "audio/mpeg, audio/mp3, .mp3",
    itemRequestProperties: (file: UploadFile<unknown> | undefined) => ({
      type: CREATE_ASSET_REQUEST_TYPE,
      organization_id: organizationId,
      category: "Music" as const,
      source: AssetSource.UIGenerated,
      description: file?.name,
      asset: {
        type: "Music" as const,
      },
    }),
    multiple: true,
    hint: <></>,
  };

  return (
    <Flex
      vertical
      gap={12}
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        width: "100%",
      }}
    >
      {rows.length > 0 ? (
        <div
          id="infinite-scroll-music"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          <InfiniteScrollList<Partial<MediaAsset>>
            dataSource={rows}
            assetLength={rows.length}
            rowKey={(asset) => asset.id ?? `no-id-${rows.indexOf(asset)}`}
            onNext={() => {
              fetchNextPage().catch((error) =>
                console.error("Error fetching next page", error)
              );
            }}
            hasMore={hasMore ?? false}
            scrollableTarget="infinite-scroll-music"
            loading={isLoadingAssetLibraryAssets}
          >
            {(asset) => {
              const rowAssetId = asset.id ?? null;
              return (
                <div style={{ width: "100%", padding: 8 }}>
                  <MusicAssetCard
                    asset={asset}
                    isBrandKit={false}
                    isSelected={
                      selectedAssetId !== undefined &&
                      selectedAssetId === rowAssetId
                    }
                    volume={volume}
                    assetLibraryId={assetLibraryId}
                    onSelect={() => onSelectAsset(rowAssetId)}
                    onDeleted={(assetId) => {
                      deleteAsset(assetId);
                      const objectUrl = previewObjectUrls.current.get(assetId);
                      if (objectUrl) {
                        URL.revokeObjectURL(objectUrl);
                        previewObjectUrls.current.delete(assetId);
                      }
                      if (selectedAssetId === assetId) {
                        onSelectAsset(null);
                      }
                    }}
                  />
                </div>
              );
            }}
          </InfiniteScrollList>
        </div>
      ) : (
        <Empty
          style={{ margin: 20 }}
          description={t(
            "components.templates.settings.musicTab.noAssetsAdded"
          )}
        />
      )}
      <UploadFiles
        {...uploadMusicProps}
        ref={uploadRef}
        showUploadList={false}
        maxFileSize={30}
        onFilesAdded={(files) => {
          setHasFilesInPreview(files.length > 0);
          if (assetLibraryId) {
            handleFileAdded(assetLibraryId, uploadRef);
          }
        }}
        onUploadComplete={(files, responses) => {
          // Insert the newly created asset into the list for immediate
          // usage without waiting for the backend to process it
          const itemRequestProperties =
            uploadMusicProps.itemRequestProperties as (
              file: UploadFile
            ) => RecursivePartial<CreateMediaAssetRequest>;
          const newAssets = responses.flatMap((response, index) => {
            const originFileObj = files[index]?.originFileObj;
            // Without the original file there's nothing to preview - skip the
            // optimistic row rather than pointing it at an empty blob.
            if (!originFileObj) return [];

            const { asset, category, source, description } =
              itemRequestProperties(originFileObj as UploadFile);

            const assetId = (response as GetOneResponse<CreateAssetResponse>)
              .data.asset_id;
            const previewObjectUrl = URL.createObjectURL(originFileObj);
            previewObjectUrls.current.set(assetId, previewObjectUrl);

            const newAsset: Partial<MediaAsset> = {
              id: assetId,
              category,
              source,
              description,
              asset_type: asset?.type,
              path: previewObjectUrl,
            };

            return [newAsset];
          });
          // optimistic update
          updateAllAssets(newAssets);

          uploadRef.current?.reset();
          // wait for UploadFiles to remove the preview display before resetting
          setTimeout(() => {
            setHasFilesInPreview(false);
          }, 500);
        }}
        style={{ flex: 1 }}
      >
        <DragAndDropArea inProgress={hasFilesInPreview} />
      </UploadFiles>
    </Flex>
  );
};
